import type {
    MultiAssetValuationDataPoint,
    NormalizedMultiAssetValuationHistory,
    SingleAssetValueOverview,
} from '@einwert/valuation-api-client'

import { assembleAssetTitle, InfoTooltip, LineChart, monetaryToDecimal, theme, tokens, ViewSwitch } from 'ComponentLibrary'
import type { LineData } from 'ComponentLibrary'
import { filterDataPointsWithUndefinedValue } from 'ComponentLibrary/src/molecules/Chart'
import { useSelectedAssets } from './useSelectedAssets'
import { useTranslation } from 'react-i18next'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'
import { Box, Stack } from '@mui/material'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import { useState } from 'react'
import {
    buildDashboardHistorySeries,
    shouldShowNormalizedDashboardHistory,
    type DashboardHistoryMode,
    type DashboardHistorySeries,
} from 'ClientPortal/src/pages/Dashboard/history-chart-data'
import { DashboardHistoryChartTooltip } from 'ClientPortal/src/pages/Dashboard/DashboardHistoryChartTooltip'

export type AnalysisGraphMetric = 'market_value' | 'rental_income'

type MultiAssetMarketValueGraphProps = {
    assets: SingleAssetValueOverview[]
    aggregatedHistory: MultiAssetValuationDataPoint[]
    normalizedHistory?: NormalizedMultiAssetValuationHistory
    metric: AnalysisGraphMetric
}

const MultiAssetMarketValueGraph = ({ assets, aggregatedHistory, normalizedHistory, metric }: MultiAssetMarketValueGraphProps) => {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const { t } = useTranslation('pages', { keyPrefix: 'analysis.analysisPage' })
    const { t: tDashboardHistoryChart } = useTranslation('pages', { keyPrefix: 'dashboard.dashboardHistoryChart' })
    const locale = useCurrentResolvedLocale()
    const { selectedAssets, getAssetColor } = useSelectedAssets()
    const [mode, setMode] = useState<DashboardHistoryMode>('absolute')

    const showSummary = selectedAssets.length === 0
    const metricLabel = tCommon(metric === 'market_value' ? 'marketValue' : 'marketRent')
    const metricColor = metric === 'market_value' ? theme.palette.primary2.green100 : tokens.ColorGraphSequentialTwoMedium
    const title = t(metric === 'market_value' ? 'selectedAssetsMarketValueDevelopment' : 'selectedAssetsMarketRentDevelopment')

    const chartSeries: LineData[] = assets
        .map((asset, index) => ({ asset, index }))
        .filter(({ asset }) => selectedAssets.includes(asset.uuid))
        .map(({ asset, index }) => {
            const assetTitle = assembleAssetTitle({ ...asset.address, customer_asset_reference: asset.customer_asset_reference })
            const mappedHistory = filterDataPointsWithUndefinedValue(
                asset.history.map((value) => ({
                    value: monetaryToDecimal(metric === 'market_value' ? value.market_value : value.rent?.income),
                    // Get date segment of ISO datetime string
                    date: value.valuation_date.slice(0, 10),
                }))
            )

            return {
                id: assetTitle,
                data: mappedHistory,
                color: getAssetColor(index, true),
            }
        })
        .filter(({ data }) => data.length > 0)

    const absoluteSeries: LineData[] = showSummary
        ? [
              {
                  id: t('summarySeriesLabel'),
                  data: aggregatedHistory.map((value) => ({
                      value: monetaryToDecimal(value[metric]),
                      date: `${value.year}`,
                  })),
                  color: metricColor,
              },
          ]
        : chartSeries
    const normalizedSeries = buildDashboardHistorySeries({
        history: aggregatedHistory,
        normalizedHistory,
        trimLeadingZeroes: true,
        series: [{ id: metricLabel, metric, color: metricColor }],
    }).normalized
    const showNormalizedHistory = showSummary && metric === 'market_value' && shouldShowNormalizedDashboardHistory(normalizedHistory)
    const activeMode = showNormalizedHistory ? mode : 'absolute'
    const activeSeries: DashboardHistorySeries = activeMode === 'normalized' ? normalizedSeries : { data: absoluteSeries }
    const historyViewItems = [
        { label: tCommon('absolute'), value: 'absolute' },
        {
            label: tCommon('relative'),
            value: 'normalized',
            rightIcon: (
                <InfoTooltip
                    text={tDashboardHistoryChart('normalizedHistoryTooltip')}
                    style={{ marginLeft: '-4px', marginRight: '-4px' }}
                />
            ),
        },
    ]

    return (
        <Stack height="100%" gap={tokens.SizingSpacing200}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={tokens.SizingSpacing200} flexWrap="wrap">
                <Headline variant="headline3" title={title} fullWidth={false} />
                {showNormalizedHistory && (
                    <ViewSwitch value={mode} onClick={(newMode) => setMode(newMode as DashboardHistoryMode)} items={historyViewItems} />
                )}
            </Stack>
            <Box flexGrow={1} minHeight={0}>
                <LineChart
                    seriesKey={`${metric}-${activeMode}`}
                    data={activeSeries.data}
                    period="year"
                    yUnit={activeMode === 'normalized' ? '' : '€'}
                    showTrends={activeMode === 'normalized'}
                    startAtZero
                    yearOnly={activeMode === 'normalized' || showSummary}
                    tooltip={activeMode === 'normalized' ? DashboardHistoryChartTooltip : undefined}
                    withYearFilter
                    yearFilteredData={activeSeries.yearFilteredData}
                    yearFilterUnitLabel={` ${tCommon('years')}`}
                    trimLeadingZeroes
                    emptyStateTitle={tCommon('notEnoughDataAvailableYet')}
                    locale={locale}
                    tooltipTranslations={{
                        draftLabel: tCommon('draft'),
                        yearLabel: tCommon('year'),
                        dateLabel: tCommon('date'),
                    }}
                />
            </Box>
        </Stack>
    )
}

export default MultiAssetMarketValueGraph
